import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import {
  ApiErrorCode,
  parseApiError,
  availabilityImportEndpoints,
  type AvailabilityImportUploadResponse,
  type AvailabilityImportPreviewResponse,
  type AvailabilityProfile,
} from '@ooh/contracts';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';
import { useApiClient } from '../../plugs/api-client';
import { apiQueryKey } from '../../data/use-api-suspense-query';
import { previewRequest } from './queries';

/**
 * Uploads the availability XLSX file via multipart POST and, on success,
 * calls `onNext` with the full upload response (job id + headers + sample
 * rows, plus `rawRows`/`detectedHeaderRowIndex` for the header-row wizard
 * step, CU-86cb1npz2) and the originally-selected `File` so the caller can
 * advance the wizard — the preview phase needs headers/sampleRows
 * client-side (e.g. for the period-label-mapping table's distinct-value
 * list, per findings.md), and the header-row phase needs the file's
 * name/size for its `SourceFileSummaryBar` (the upload response itself
 * carries neither). Upload errors (size, MIME type, permission) surface as
 * a toast via `parseApiError()`, per availability-import-ui's "Upload
 * phase" requirement.
 */
export function useUploadAvailabilityFile(
  onNext: (response: AvailabilityImportUploadResponse, file: File) => void,
) {
  const api = useApiClient();
  return useMutation({
    mutationFn: (file: File) => api.upload(availabilityImportEndpoints.upload, file),
    onSuccess: (data, file) => onNext(data, file),
    onError: (error: unknown) =>
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}

/**
 * Re-runs column recognition "from scratch" for an import job, bypassing any
 * remembered `availability_profile_mappings` row for this file's column
 * signature — `POST /availability-import/:id/resolve-fresh`. Returns the
 * same shape as the preview endpoint, always `mappingSource: 'resolved'`. Wired to
 * the "Wyczyść mapowanie" action on `RememberedMappingBanner`
 * (CU-86cbafj4j) — the caller (`preview-phase.tsx`) swaps the returned
 * response in as the new `initialData` and remounts `PreviewPhaseEditor` so
 * every downstream editor/rematch hook reseeds from the fresh profile.
 *
 * Accepts the same optional `headerRowIndex` override the preview request does
 * (CU-86cb1npz2, gap #1) — the caller forwards the current profile's
 * `headerRowIndex` so a "Wyczyść mapowanie" reset re-parses against the
 * operator's already-confirmed header row instead of silently reverting to
 * `detectHeaderRow`'s auto-detected one.
 */
export function useResolveFreshAvailabilityProfile(
  jobId: number,
  onSuccess: (response: AvailabilityImportPreviewResponse) => void,
) {
  const api = useApiClient();
  return useMutation({
    mutationFn: (headerRowIndex: number | undefined) =>
      api.call(availabilityImportEndpoints.resolveFresh, previewRequest(jobId, headerRowIndex)),
    onSuccess,
    onError: (error: unknown) =>
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}

/**
 * Fetches `GET /availability-import/:id/preview` with the operator's chosen
 * `headerRowIndex` (CU-86cb1npz2, gap #1) and primes the TanStack Query
 * cache under the exact same key preview query uses for that
 * `(jobId, headerRowIndex)` pair — so when the wizard renders `PreviewPhase`
 * with the same `headerRowIndex`, its own `useApiSuspenseQuery` finds the response already cached (`staleTime: Infinity`) instead of
 * re-fetching. `headerRowIndex` is `undefined` for the common case where the
 * operator kept the auto-detected row, matching the preview request's own
 * "omit when unchanged" contract.
 */
export function usePreviewWithHeaderRowOverride(
  jobId: number,
  onSuccess: (headerRowIndex: number | undefined) => void,
) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (headerRowIndex: number | undefined) => {
      const request = previewRequest(jobId, headerRowIndex);
      const response = await api.call(availabilityImportEndpoints.preview, request);
      queryClient.setQueryData(apiQueryKey(availabilityImportEndpoints.preview, request), response);
      return headerRowIndex;
    },
    onSuccess: (headerRowIndex) => onSuccess(headerRowIndex),
    onError: (error: unknown) =>
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}

/**
 * Confirms the (possibly edited) profile for an import job, enqueuing the
 * worker job on success. `markRestUnavailable` is only meaningful for
 * `timeEncoding = 'global'` + `polarity = 'list_of_free'` profiles — the api
 * rejects it outside that scope (server-enforced, not just a UI gate).
 *
 * `PERIOD_LABEL_MAP_INCOMPLETE`, `GLOBAL_PERIOD_REQUIRED`, and
 * `MARK_REST_UNAVAILABLE_OUT_OF_SCOPE` each get a dedicated translated toast
 * rather than the generic parsed message — per packages/contracts/AGENTS.md's
 * "translated onError handler for every new ApiErrorCode" MUST rule. The
 * client-side gating (`usePreviewProfileEditor`'s `periodLabelIncomplete`,
 * the global-range date pickers, and the mark-rest-unavailable checkbox's
 * own scope gating) normally prevents these from ever reaching the server;
 * these handlers are defense in depth for the rare case one does (e.g. a
 * stale/edited profile). `ALL_ROWS_UNMATCHED` (CU-86cbafj4j) is the same
 * kind of defense in depth for `preview-phase.tsx`'s client-side
 * `allRowsUnmatched` gate — this handler only fires if that gate somehow
 * didn't block the click first.
 */
export function useConfirmAvailabilityImport(jobId: number, onSuccess: () => void) {
  const api = useApiClient();
  const { t } = useTranslation('availabilityImport');
  return useMutation({
    mutationFn: (input: { profile: AvailabilityProfile; markRestUnavailable: boolean }) =>
      api.call(availabilityImportEndpoints.confirm, { id: jobId, ...input }),
    onSuccess,
    onError: (error: unknown) => {
      const parsed = parseApiError(error);
      switch (parsed.errorCode) {
        case ApiErrorCode.PERIOD_LABEL_MAP_INCOMPLETE:
          toast.error(t('toasts.periodLabelMapIncomplete'));
          return;
        case ApiErrorCode.GLOBAL_PERIOD_REQUIRED:
          toast.error(t('toasts.globalPeriodRequired'));
          return;
        case ApiErrorCode.MARK_REST_UNAVAILABLE_OUT_OF_SCOPE:
          toast.error(t('toasts.markRestUnavailableOutOfScope'));
          return;
        case ApiErrorCode.DATE_RANGE_COLUMNS_REQUIRED:
          toast.error(t('toasts.dateRangeColumnsRequired'));
          return;
        case ApiErrorCode.MONTH_PERIOD_COLUMNS_INCOMPLETE:
          toast.error(t('toasts.monthPeriodColumnsIncomplete'));
          return;
        case ApiErrorCode.SUPPLIER_NOT_RESOLVABLE:
          toast.error(t('toasts.supplierNotResolvable'));
          return;
        case ApiErrorCode.ALL_ROWS_UNMATCHED:
          toast.error(t('toasts.allRowsUnmatched'));
          return;
        default:
          toast.error(apiErrorToUserMessage(parsed, 'mutation'));
      }
    },
  });
}
